// Fetch wrapper that attaches the Supabase JWT to every API request.
import { supabase } from './supabaseClient';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// A rejected token means the session is gone (expired, or revoked server-side).
// Clear it so the app falls back to the login screen instead of showing errors.
async function handleUnauthorized(status) {
  if (status !== 401) return;
  const { data: { session } } = await supabase.auth.getSession();
  if (session) await supabase.auth.signOut();
}

async function request(path, { method = 'GET', body, params } = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const url = new URL(`${BASE}${path}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
    });
  }

  const res = await fetch(url.toString(), {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    await handleUnauthorized(res.status);
    const err = new Error(json?.error || `Request failed (${res.status})`);
    err.code = json?.code; // e.g. 'mfa_required' — lets the client branch on it
    throw err;
  }
  return json;
}

// POST that reads a newline-delimited JSON stream, calling `onEvent` for each
// line as it arrives rather than waiting for the whole body. Used by Ask, where
// the figures are ready well before the sentence that summarises them.
async function stream(path, body, onEvent) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  // Errors raised before streaming began still arrive as a normal JSON body.
  if (!res.ok) {
    const text = await res.text();
    let json = null;
    try { json = text ? JSON.parse(text) : null; } catch { /* not JSON */ }
    await handleUnauthorized(res.status);
    const err = new Error(json?.error || `Request failed (${res.status})`);
    err.code = json?.code;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // Lines can be split across chunks, so only whole lines are parsed and the
  // remainder is carried over to the next read.
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      if (!line.trim()) continue;
      onEvent(JSON.parse(line));
    }
  }
  if (buffer.trim()) onEvent(JSON.parse(buffer));
}

export const api = {
  get: (path, params) => request(path, { params }),
  post: (path, body) => request(path, { method: 'POST', body }),
  put: (path, body) => request(path, { method: 'PUT', body }),
  patch: (path, body) => request(path, { method: 'PATCH', body }),
  del: (path) => request(path, { method: 'DELETE' }),
  stream,
};
